import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';

/**
 * Validates if the stored Zendesk JWT token is valid and not expired
 * @returns {boolean} true if token is valid, false otherwise
 */
export const isZendeskTokenValid = (): boolean => {
    try {
        // Get stored Zendesk token
        const zendeskToken = localStorageUtil.getAny(ELocalStorageKeys.ZENDESK_TOKEN);

        if (!zendeskToken) {
            console.log('❌ No Zendesk token found');
            return false;
        }

        // Check JWT format (should have 3 parts separated by dots)
        const parts = zendeskToken.split('.');
        if (parts.length !== 3) {
            console.log('❌ Invalid JWT format - wrong number of parts');
            return false;
        }

        try {
            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1]));
            console.log('🔍 JWT Payload:', payload);

            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

            // Check if token has expiration time (exp) and if it's expired
            if (payload.exp) {
                if (payload.exp < currentTime) {
                    console.log('❌ Zendesk token expired at:', new Date(payload.exp * 1000));
                    return false;
                }
                console.log('✅ Token expires at:', new Date(payload.exp * 1000));
            }

            // Check if token has issued at time (iat)
            if (payload.iat) {
                const tokenAge = currentTime - payload.iat;
                const maxAge = 24 * 60 * 60; // 24 hours in seconds

                console.log(`🕐 Token issued at: ${new Date(payload.iat * 1000)}`);
                console.log(`⏱️  Token age: ${Math.floor(tokenAge / 3600)} hours`);

                if (tokenAge > maxAge) {
                    console.log('❌ Zendesk token too old (more than 24 hours)');
                    return false;
                }
            }

            // Check for required fields
            if (!payload.email || !payload.name) {
                console.log('❌ Token missing required fields (email, name)');
                return false;
            }

            console.log('✅ Zendesk token is valid');
            return true;

        } catch (decodeError) {
            console.error('❌ Failed to decode Zendesk token payload:', decodeError);
            return false;
        }

    } catch (error) {
        console.error('❌ Error checking Zendesk token validity:', error);
        return false;
    }
};

/**
 * Decodes a JWT token payload without validation
 * @param {string} token - The JWT token to decode
 * @returns {any} The decoded payload or null if invalid
 */
export const decodeJwtPayload = (token: string): any => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        return JSON.parse(atob(parts[1]));
    } catch (error) {
        console.error('Failed to decode JWT payload:', error);
        return null;
    }
};

/**
 * Checks if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} true if expired, false if still valid
 */
export const isJwtTokenExpired = (token: string): boolean => {
    try {
        const payload = decodeJwtPayload(token);
        if (!payload || !payload.exp) return false; // No expiration = not expired

        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
    } catch (error) {
        return true; // Assume expired if we can't decode
    }
};